import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import './CaseManagement.css';

const CaseAdvocateMapping = () => {
  const { cases, setCases, advocates, addAuditLog } = useApp();
  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    advocateId: '',
    role: 'Lead',
    partyId: '',
  });
  const [editingId, setEditingId] = useState(null);

  const selectedCase = cases.find(c => c.id === selectedCaseId);
  const mappings = selectedCase?.advocateMappings || [];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!selectedCaseId) {
      alert('Please select a case first');
      return;
    }

    const advocate = advocates.find(a => a.id === formData.advocateId);
    const party = selectedCase?.parties?.find(p => p.id === formData.partyId);
    
    const mapping = {
      id: editingId || Date.now().toString(),
      ...formData,
      advocateName: advocate?.advocateName || '',
      partyName: party?.partyName || '',
    };

    if (editingId) {
      setCases(cases.map(c => 
        c.id === selectedCaseId
          ? {
              ...c,
              advocateMappings: (c.advocateMappings || []).map(m => 
                m.id === editingId ? mapping : m
              )
            }
          : c
      ));
      addAuditLog('Advocate Mapping Updated', `Advocate mapping updated for case ${selectedCase?.caseNumber}`);
    } else {
      setCases(cases.map(c => 
        c.id === selectedCaseId
          ? {
              ...c,
              advocateMappings: [...(c.advocateMappings || []), mapping]
            }
          : c
      ));
      addAuditLog('Advocate Mapping Added', `Advocate mapping added for case ${selectedCase?.caseNumber}`);
    }
    
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      advocateId: '',
      role: 'Lead',
      partyId: '',
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (mapping) => {
    setFormData(mapping);
    setEditingId(mapping.id);
    setShowForm(true);
  };

  const handleDelete = (mappingId) => {
    setCases(cases.map(c => 
      c.id === selectedCaseId
        ? {
            ...c,
            advocateMappings: (c.advocateMappings || []).filter(m => m.id !== mappingId)
          }
        : c
    ));
    addAuditLog('Advocate Mapping Deleted', `Advocate mapping deleted for case ${selectedCase?.caseNumber}`);
  };

  return (
    <div className="case-container">
      <div className="case-header">
        <h1>🔗 Case-Advocate Mapping</h1>
      </div>

      <div className="case-form-card">
        <h2>Select Case</h2>
        <div className="form-group">
          <select
            value={selectedCaseId}
            onChange={(e) => {
              setSelectedCaseId(e.target.value);
              setShowForm(false);
              resetForm();
            }}
            className="case-select"
          >
            <option value="">Select a case</option>
            {cases.map(c => (
              <option key={c.id} value={c.id}>
                {c.caseNumber} - {c.caseTitle}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedCaseId && (
        <>
          <div className="case-header">
            <h2>Case: {selectedCase?.caseNumber} - {selectedCase?.caseTitle}</h2>
            <button onClick={() => setShowForm(!showForm)} className="btn-primary">
              {showForm ? 'Cancel' : '+ Add Mapping'}
            </button>
          </div>

          {showForm && (
            <div className="case-form-card">
              <h2>{editingId ? 'Edit Mapping' : 'Add New Mapping'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Party *</label>
                    <select
                      value={formData.partyId}
                      onChange={(e) => setFormData({...formData, partyId: e.target.value})}
                      required
                    >
                      <option value="">Select Party</option>
                      {(selectedCase?.parties || []).map(party => (
                        <option key={party.id} value={party.id}>
                          {party.partyName} ({party.partyType})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Advocate *</label>
                    <select
                      value={formData.advocateId}
                      onChange={(e) => setFormData({...formData, advocateId: e.target.value})}
                      required
                    >
                      <option value="">Select Advocate</option>
                      {advocates.map(advocate => (
                        <option key={advocate.id} value={advocate.id}>
                          {advocate.advocateName} - {advocate.specialization || 'N/A'}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Role *</label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({...formData, role: e.target.value})}
                      required
                    >
                      <option value="Lead">Lead Advocate</option>
                      <option value="Assistant">Assistant Advocate</option>
                    </select>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-primary">
                    {editingId ? 'Update' : 'Add'} Mapping
                  </button>
                  <button type="button" onClick={resetForm} className="btn-secondary">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="case-table-card">
            <h2>Advocate Mappings ({mappings.length})</h2>
            {mappings.length > 0 ? (
              <div className="table-container">
                <table className="case-table">
                  <thead>
                    <tr>
                      <th>Party</th>
                      <th>Advocate</th>
                      <th>Role</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mappings.map(mapping => (
                      <tr key={mapping.id}>
                        <td><strong>{mapping.partyName}</strong></td>
                        <td>{mapping.advocateName}</td>
                        <td>
                          <span className={`role-badge ${mapping.role === 'Lead' ? 'lead' : 'assistant'}`}>
                            {mapping.role}
                          </span>
                        </td>
                        <td>
                          <button onClick={() => handleEdit(mapping)} className="btn-edit">Edit</button>
                          <button onClick={() => handleDelete(mapping.id)} className="btn-delete">Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="empty-state">No advocate mappings yet. Click "Add Mapping" to get started.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CaseAdvocateMapping;

